import { createEvent, createStore, createEffect } from 'effector';
import type { Locale } from '../shared/locale';
import {
  DEFAULT_LOCALE,
  LOCALE_TEXT_DIRECTION,
  normalizeLocale,
} from '../shared/locale';
import { setOrderedSightIds, setSights } from './appState';
import { requestAppConfig, type AppClientConfig } from '../services/configClient';
import type { Sight, SightId } from '../types';

const readLocaleFromUrl = (): Locale => {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }
  const params = new URLSearchParams(window.location.search);
  const raw =
    params.get('lang') ?? params.get('locale') ?? params.get('language');
  return normalizeLocale(raw);
};

const updateUrlLocaleParam = (locale: Locale) => {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  if (url.searchParams.get('lang') === locale) {
    return;
  }
  url.searchParams.set('lang', locale);
  window.history.replaceState({}, '', url.toString());
};

const applyLocaleSideEffects = (
  locale: Locale,
  options: { updateUrl?: boolean } = {}
) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
    document.documentElement.dir = LOCALE_TEXT_DIRECTION[locale];
  }
  if (options.updateUrl !== false) {
    updateUrlLocaleParam(locale);
  }
};

export const setLocale = createEvent<Locale>();
export const $locale = createStore<Locale>(readLocaleFromUrl()).on(
  setLocale,
  (_, locale) => locale
);

const fetchConfigFx = createEffect<Locale, AppClientConfig>(async (locale) =>
  requestAppConfig(locale)
);

/**
 * Becomes `true` after the first `/api/config` response settles (success OR
 * failure). Used to gate splat/GLB scene mount: rendering before sights are
 * known triggers a useEffect re-run when sights arrive (new array reference),
 * which disposes the still-loading scene mid-flight and crashes async loaders
 * with `scene.add(null)`. Using `.finally` (not `.done`) ensures the scene
 * still opens if the config endpoint is unreachable.
 */
export const $configFetched = createStore(false).on(
  fetchConfigFx.finally,
  () => true
);

let hasLocaleBeenApplied = false;
$locale.watch((locale) => {
  applyLocaleSideEffects(locale, { updateUrl: hasLocaleBeenApplied });
  fetchConfigFx(locale);
  hasLocaleBeenApplied = true;
});

fetchConfigFx.doneData.watch((config) => {
  const sights = config.pois;
  const orderedIds = sights.map((sight) => sight.id as SightId);
  const byId = sights.reduce<Record<SightId, Sight>>((acc, sight) => {
    acc[sight.id] = sight;
    return acc;
  }, {});
  setOrderedSightIds(orderedIds);
  setSights(byId);
});
